import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, Send, Download, CheckCircle, Circle, ExternalLink, Ban, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { MonthPicker, MONTHS } from "@/components/MonthPicker";
import { MarkPaidDialog } from "@/components/MarkPaidDialog";
import { VoidDialog } from "@/components/VoidDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, money, fmtDate } from "@/lib/api";

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export default function ReportsPage() {
  const { user } = useAuth();
  const now = new Date();
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [paying, setPaying] = useState(null);
  const [voiding, setVoiding] = useState(null);
  const [view, setView] = useState("active");

  const restore = async (inv) => {
    setBusy(inv.id);
    try {
      const status = inv.sent_at ? "sent" : "draft";
      await api.patch(`/invoices/${inv.id}/status`, { status });
      toast.success(status === "draft" ? `${inv.invoice_number} restored — find it on the Drafts tab` : `${inv.invoice_number} restored`);
      load();
    }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const load = useCallback(() => {
    api.get("/reports/monthly", { params: ym }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [ym]);
  useEffect(load, [load]);

  const resend = async (inv) => {
    setBusy(inv.id);
    try { const r = await api.post(`/invoices/${inv.id}/send`); toast.success(`Emailed to ${r.data.sent_to}`); load(); }
    catch (e) { toast.error(errMsg(e), { duration: 9000 }); } finally { setBusy(""); }
  };

  const togglePaid = async (inv) => {
    if (inv.status !== "paid") { setPaying(inv); return; }
    setBusy(inv.id);
    try { await api.patch(`/invoices/${inv.id}/status`, { status: "sent" }); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const exportCsv = () => {
    const rows = [["Date", "Invoice #", "Client", "Description", "Subtotal", "Tax", "Total", "Status", "Sent to"]];
    data.invoices.forEach((i) => rows.push([i.date, i.invoice_number, i.client?.name, i.line_items.map((l) => l.description).join("; "),
      i.subtotal.toFixed(2), i.tax_amount.toFixed(2), i.total.toFixed(2), i.status === "void" ? "Void" : i.status === "paid" ? "Paid" : "Unpaid", i.sent_to || ""]));
    rows.push([], ["Month total", "", "", "", "", "", data.totals.total.toFixed(2)], ["Paid", "", "", "", "", "", data.totals.paid.toFixed(2)], ["Unpaid", "", "", "", "", "", data.totals.unpaid.toFixed(2)]);
    const blob = new Blob([rows.map((r) => r.map(csvEscape).join(",")).join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `invoices-${ym.year}-${String(ym.month).padStart(2, "0")}.csv` });
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const label = `${MONTHS[ym.month - 1]} ${ym.year}`;
  const shownRows = data ? data.invoices.filter((i) => view === "all" || (view === "void" ? i.status === "void" : i.status !== "void")) : [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 print:hidden">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Monthly Report</h1>
          <p className="text-sm text-muted-foreground mt-1">Every invoice by date, with totals for the month and the year.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="report-export-csv-button" variant="outline" onClick={exportCsv} disabled={!data}><Download className="h-4 w-4 mr-2" />CSV</Button>
          <Button data-testid="report-print-button" onClick={() => window.print()} disabled={!data} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"><Printer className="h-4 w-4 mr-2" />Print</Button>
        </div>
      </div>

      <MonthPicker value={ym} onChange={setYm} prefix="report" />

      {!data ? <div className="text-sm text-muted-foreground">Loading report...</div> : (
        <div className="print-area space-y-6">
          <div className="hidden print:block">
            <h1 className="text-2xl font-extrabold">{user?.business_name}</h1>
            <p className="text-sm text-slate-600">Invoice report — {label}</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["Invoices", data.totals.count, ""], ["Invoiced", money(data.totals.total), ""], ["Paid", money(data.totals.paid), "text-green-600"], ["Unpaid", money(data.totals.unpaid), "text-amber-600"]].map(([l, v, c]) => (
              <div key={l} className="surface p-4 print:border-slate-300" data-testid={`report-stat-${l.toLowerCase()}`}>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{l}</div>
                <div className={`font-mono font-bold text-xl mt-1 ${c}`}>{v}</div>
              </div>
            ))}
          </div>

          <div className="surface p-4 sm:p-5 print:border-slate-300 grid sm:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-3" data-testid="report-profit-summary">
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{MONTHS[ym.month - 1]} income</div>
              <div className="font-mono font-bold text-xl mt-1">{money(data.totals.total)}</div>
            </div>
            <div className="hidden sm:block text-2xl text-muted-foreground font-light">−</div>
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Expenses</div>
              <div className="font-mono font-bold text-xl mt-1 text-red-600" data-testid="report-stat-expenses">{money(data.expenses.month)}</div>
            </div>
            <div className="hidden sm:block text-2xl text-muted-foreground font-light">=</div>
            <div className={`rounded-lg px-4 py-3 ${data.profit.month >= 0 ? "bg-green-50 dark:bg-green-950/40" : "bg-red-50 dark:bg-red-950/40"}`}>
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Net profit</div>
              <div className={`font-mono font-bold text-2xl mt-1 ${data.profit.month >= 0 ? "text-green-600" : "text-red-600"}`} data-testid="report-stat-profit">{money(data.profit.month)}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{ym.year} YTD: <span className={`font-mono font-semibold ${data.profit.year >= 0 ? "text-green-600" : "text-red-600"}`} data-testid="report-year-profit">{money(data.profit.year)}</span></div>
            </div>
          </div>

          <div className="surface overflow-x-auto print:border-slate-300">
            <div className="sticky left-0 flex gap-2 flex-wrap p-3 border-b border-border print:hidden">
              {[["active", "Active"], ["void", `Voided (${data.totals.void_count})`], ["all", "All"]].map(([k, l]) => (
                <button key={k} data-testid={`report-view-filter-${k}`} onClick={() => setView(k)}
                  className={`h-8 px-3 rounded-full text-xs font-semibold border ${view === k ? "bg-slate-900 text-white border-slate-900 dark:bg-white dark:text-slate-900" : "border-border hover:bg-secondary"}`}>{l}</button>
              ))}
            </div>
            <table className="w-full text-xs md:text-sm print:table" data-testid="report-table">
              <thead>
                <tr className="border-b border-border text-[11px] md:text-xs font-bold uppercase tracking-wider text-muted-foreground text-left">
                  <th className="sticky left-0 z-10 bg-card px-3 md:px-4 py-3 border-r border-border md:border-r-0 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)] md:shadow-none">Client</th>
                  <th className="hidden md:table-cell print:table-cell px-3 md:px-4 py-3">Invoice #</th>
                  <th className="px-3 md:px-4 py-3">Date</th>
                  <th className="px-3 md:px-4 py-3 text-right">Amount</th>
                  <th className="px-3 md:px-4 py-3">Status</th>
                  <th className="px-3 md:px-4 py-3 print:hidden">Actions</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-sm text-muted-foreground" data-testid="report-empty-state">{view === "void" ? `No voided invoices in ${label}.` : `No invoices dated in ${label}.`}</td></tr>
                )}
                {shownRows.map((inv) => (
                  <tr key={inv.id} data-testid={`report-row-${inv.id}`} className={`border-b border-border last:border-0 hover:bg-secondary/50 ${inv.status === "void" ? "opacity-60" : ""}`}>
                    <td className="sticky left-0 z-10 bg-card px-3 md:px-4 py-2.5 md:py-3 max-w-[150px] md:max-w-none border-r border-border md:border-r-0 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)] md:shadow-none" data-testid={`report-client-${inv.id}`}>
                      <div className="font-medium text-sm truncate">{inv.client?.name}</div>
                      <div className="md:hidden print:hidden font-mono text-[11px] font-semibold text-foreground/80">{inv.invoice_number}</div>
                      <div className="text-[11px] md:text-xs text-muted-foreground truncate md:max-w-[220px]">{inv.status === "void" && inv.void_reason ? `Voided: ${inv.void_reason}` : inv.line_items.map((l) => l.description).join(", ")}</div>
                    </td>
                    <td className="hidden md:table-cell print:table-cell px-3 md:px-4 py-2.5 md:py-3 font-mono font-semibold whitespace-nowrap">{inv.invoice_number}</td>
                    <td className="px-3 md:px-4 py-2.5 md:py-3 whitespace-nowrap">{fmtDate(inv.date)}</td>
                    <td className={`px-3 md:px-4 py-2.5 md:py-3 text-right font-mono font-bold text-sm whitespace-nowrap ${inv.status === "void" ? "line-through text-muted-foreground" : ""}`}>{money(inv.total)}</td>
                    <td className="px-3 md:px-4 py-2.5 md:py-3">
                      <span className="print:hidden"><StatusBadge status={inv.status} id={inv.id} /></span>
                      <span className="hidden print:inline font-semibold">{inv.status === "void" ? "Void" : inv.status === "paid" ? "Paid" : "Unpaid"}</span>
                    </td>
                    <td className="px-3 md:px-4 py-2.5 md:py-3 print:hidden">
                      <div className="flex items-center gap-1">
                        {inv.status === "void" ? (
                          <button data-testid={`report-restore-${inv.id}`} onClick={() => restore(inv)} disabled={busy === inv.id} title="Restore invoice"
                            className="h-9 md:h-8 px-2 rounded-lg text-xs font-semibold flex items-center gap-1 text-emerald-700 hover:bg-secondary">
                            <RotateCcw className="h-4 w-4" /><span className="hidden lg:inline">Restore</span>
                          </button>
                        ) : (<>
                        <button data-testid={`report-toggle-paid-${inv.id}`} onClick={() => togglePaid(inv)} disabled={busy === inv.id} title={inv.status === "paid" ? "Mark unpaid" : "Mark paid"}
                          className={`h-9 md:h-8 px-2 rounded-lg text-xs font-semibold flex items-center gap-1 hover:bg-secondary ${inv.status === "paid" ? "text-green-600" : "text-muted-foreground"}`}>
                          {inv.status === "paid" ? <CheckCircle className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                          <span className="hidden lg:inline">{inv.status === "paid" ? "Paid" : "Mark paid"}</span>
                        </button>
                        <button data-testid={`report-resend-${inv.id}`} onClick={() => resend(inv)} disabled={busy === inv.id || !inv.client?.email} title={inv.client?.email ? "Resend by email" : "Client has no email"}
                          className="h-9 md:h-8 px-2 rounded-lg text-xs font-semibold flex items-center gap-1 text-amber-700 dark:text-amber-400 hover:bg-secondary disabled:opacity-40">
                          <Send className="h-4 w-4" /><span className="hidden lg:inline">{busy === inv.id ? "..." : "Resend"}</span>
                        </button>
                        <button data-testid={`report-void-${inv.id}`} onClick={() => setVoiding(inv)} disabled={busy === inv.id} title="Void invoice"
                          className="h-9 md:h-8 px-2 rounded-lg text-xs font-semibold flex items-center gap-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40">
                          <Ban className="h-4 w-4" /><span className="hidden lg:inline">Void</span>
                        </button>
                        </>)}
                        <Link to={`/invoices/${inv.id}`} data-testid={`report-view-${inv.id}`} className="h-9 w-9 md:h-8 md:w-8 grid place-items-center rounded-lg hover:bg-secondary text-muted-foreground" title="Open invoice"><ExternalLink className="h-4 w-4" /></Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {data.invoices.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-slate-900 dark:border-slate-200 font-bold text-sm">
                    <td className="sticky left-0 z-10 bg-card px-3 md:px-4 py-3 border-r border-border md:border-r-0 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.15)] md:shadow-none">Total for {label}</td>
                    <td className="hidden md:table-cell print:table-cell px-3 md:px-4 py-3"></td>
                    <td className="px-3 md:px-4 py-3"></td>
                    <td className="px-3 md:px-4 py-3 text-right font-mono text-lg whitespace-nowrap" data-testid="report-month-total">{money(data.totals.total)}</td>
                    <td className="px-3 md:px-4 py-3 text-xs font-semibold whitespace-nowrap" colSpan={2}>Paid {money(data.totals.paid)} · Unpaid {money(data.totals.unpaid)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          <div className="surface overflow-x-auto print:border-slate-300 print:break-before-page">
            <div className="px-4 pt-4 flex items-baseline justify-between">
              <h2 className="text-lg font-semibold">{ym.year} year-to-date</h2>
              <span className="text-xs text-muted-foreground">{data.year_totals.count} invoices</span>
            </div>
            <table className="w-full text-sm mt-2" data-testid="report-year-table">
              <thead>
                <tr className="border-b border-border text-xs font-bold uppercase tracking-wider text-muted-foreground text-left">
                  <th className="px-4 py-2">Month</th><th className="px-4 py-2 text-right hidden sm:table-cell">Invoices</th><th className="px-4 py-2 text-right">Invoiced</th><th className="px-4 py-2 text-right hidden sm:table-cell">Paid</th><th className="px-4 py-2 text-right hidden sm:table-cell">Unpaid</th><th className="px-4 py-2 text-right">Expenses</th><th className="px-4 py-2 text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {data.months.map((m) => (
                  <tr key={m.month} data-testid={`report-year-row-${m.month}`} onClick={() => setYm({ ...ym, month: m.month })}
                    className={`border-b border-border last:border-0 cursor-pointer hover:bg-secondary/50 ${m.month === ym.month ? "bg-emerald-50 dark:bg-emerald-950/30" : ""} ${m.count === 0 ? "text-muted-foreground" : ""}`}>
                    <td className="px-4 py-2 font-medium">{MONTHS[m.month - 1]}</td>
                    <td className="px-4 py-2 text-right font-mono hidden sm:table-cell">{m.count}</td>
                    <td className="px-4 py-2 text-right font-mono">{money(m.total)}</td>
                    <td className="px-4 py-2 text-right font-mono text-green-600 hidden sm:table-cell">{money(m.paid)}</td>
                    <td className="px-4 py-2 text-right font-mono text-amber-600 hidden sm:table-cell">{money(m.unpaid)}</td>
                    <td className="px-4 py-2 text-right font-mono text-red-600">{money(m.expenses)}</td>
                    <td className={`px-4 py-2 text-right font-mono font-semibold ${m.profit < 0 ? "text-red-600" : "text-green-600"}`}>{money(m.profit)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-900 dark:border-slate-200 font-bold">
                  <td className="px-4 py-3">Year total</td>
                  <td className="px-4 py-3 text-right font-mono hidden sm:table-cell">{data.year_totals.count}</td>
                  <td className="px-4 py-3 text-right font-mono" data-testid="report-year-total">{money(data.year_totals.total)}</td>
                  <td className="px-4 py-3 text-right font-mono text-green-600 hidden sm:table-cell">{money(data.year_totals.paid)}</td>
                  <td className="px-4 py-3 text-right font-mono text-amber-600 hidden sm:table-cell">{money(data.year_totals.unpaid)}</td>
                  <td className="px-4 py-3 text-right font-mono text-red-600" data-testid="report-year-expenses">{money(data.expenses.year)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${data.profit.year < 0 ? "text-red-600" : "text-green-600"}`}>{money(data.profit.year)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
      {paying && <MarkPaidDialog invoice={paying} onClose={() => setPaying(null)} onDone={() => load()} />}
      {voiding && <VoidDialog invoice={voiding} onClose={() => setVoiding(null)} onDone={() => load()} />}
    </div>
  );
}
